import { AUTH_PATHS } from "@/constants/paths";
import { Pages } from "@/constants/enums";
import useFormFields from "@/hooks/useFormFields";
import useFormValidations from "@/hooks/useFormValidations";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/features/auth/store";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/shared/loader";
import FormFields from "@/components/shared/form-fields/form-fields";
import { authService } from "@/features/auth/services/authService";
import { resetPasswordSchema } from "@/validations/password-reset";
import type { z } from "zod";
import { cn } from "@/lib/utils";
import { Form } from "@/components/ui/form";
import { getErrorMessage } from "@/lib/error-handler";
import { AlertCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

type ResetPasswordSchema = z.infer<typeof resetPasswordSchema>;

export default function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token")?.trim() ?? "";

  // Password recovery is an unauthenticated flow: drop any lingering session so
  // "back to sign in" and the post-reset redirect land on the login screen
  // instead of bouncing an already-signed-in tester to /admin.
  const clearAuth = useAuthStore((s) => s.clearAuth);
  useEffect(() => {
    clearAuth();
  }, [clearAuth]);

  const { getFormFields } = useFormFields({ slug: Pages.RESET_PASSWORD });
  const { getValidationSchema } = useFormValidations({
    slug: Pages.RESET_PASSWORD,
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const formMethods = useForm<ResetPasswordSchema>({
    resolver: zodResolver(getValidationSchema() as typeof resetPasswordSchema),
    mode: "onChange",
    defaultValues: {
      password: "",
      confirm_password: "",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = formMethods;

  const onSubmit = async (data: ResetPasswordSchema) => {
    setErrorMessage(null);

    try {
      await authService.resetPassword(token, data.password);
      toast.success("تم إعادة تعيين كلمة المرور بنجاح");
      navigate(AUTH_PATHS.LOGIN, { replace: true });
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  if (!token) {
    return (
      <div
        className={cn(
          "flex flex-col bg-card p-4 rounded-lg shadow w-full max-w-md",
        )}
      >
        <div className="mb-4 space-y-1 text-center">
          <h2 className="text-lg font-semibold text-foreground">
            رابط غير صالح
          </h2>
          <p className="text-sm text-muted-foreground">
            رابط إعادة تعيين كلمة المرور غير صالح أو منتهي الصلاحية.
          </p>
        </div>

        <Button asChild className="w-full">
          <Link to={AUTH_PATHS.FORGOT_PASSWORD}>طلب رابط جديد</Link>
        </Button>

        <Button asChild variant="ghost" className="mt-2">
          <Link to={AUTH_PATHS.LOGIN}>
            <ArrowRight className="ml-2 h-4 w-4" />
            العودة لتسجيل الدخول
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <Form {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn(
          "flex flex-col bg-card p-4 rounded-lg shadow w-full max-w-md",
        )}
      >
        <div className="mb-4 space-y-1 text-center">
          <h2 className="text-lg font-semibold text-foreground">
            إعادة تعيين كلمة المرور
          </h2>
          <p className="text-sm text-muted-foreground">
            أدخل كلمة المرور الجديدة لحسابك
          </p>
        </div>

        {errorMessage && (
          <div className="flex items-start gap-2 mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {getFormFields().map((field) => (
          <div key={field.name}>
            <FormFields {...field} control={control} errors={errors} />
          </div>
        ))}

        <Button
          type="submit"
          disabled={isSubmitting}
          className={cn("w-full h-10 mt-4 text-white font-medium")}
        >
          <LoadingButton
            loading={isSubmitting}
            loadingText="جار الحفظ..."
            loaderSize="sm"
          >
            حفظ كلمة المرور
          </LoadingButton>
        </Button>

        <Button asChild variant="ghost" className="mt-2">
          <Link to={AUTH_PATHS.LOGIN}>
            <ArrowRight className="ml-2 h-4 w-4" />
            العودة لتسجيل الدخول
          </Link>
        </Button>
      </form>
    </Form>
  );
}
